import Activities, { ActivityPayload } from '@/components/Activities'
import Drawer from '@/components/Drawer'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import ProductGroupSelect from '@/components/Form/Shared/ProductGroupSelect'
import { Button } from '@/components/ui/button'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import CloseIcons from '@/icons/CloseIcons'
import CustomerIcons from '@/icons/CustomerIcons'
import InfoIcons from '@/icons/InfoIcons'
import useAuthStore from '@/stores/useAuthStore'
import { formatDateWithFormatter } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import {
    Ban,
    Check,
    CheckCircle2,
    Clock,
    ExternalLink,
    Flame,
    Loader2,
    Snowflake,
    UserPlus,
    UserX,
    X,
} from 'lucide-react'
import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
    Button as VNButton,
    GridHookForm,
    IconButton,
    Tabs,
    Typogaphy,
} from 'vnetwork-ui'
import ContactConversation from '../../../modules/Marketing/components/Detail/ContactConversation'
import LivechatConversationPreview from '../../../modules/Marketing/components/Detail/LivechatConversationPreview'
import LeadConversionFlowCompact from '../../../modules/Marketing/components/Detail/LeadConversionFlowCompact'
import EmailFieldUpdate from '../../../modules/Marketing/components/List/EmailFieldUpdate'
import {
    classifyContactSource,
    getContactSourceByID,
    getContactSourceUpdates,
    LeadStatus,
    updateContactSource,
} from '../../../modules/Marketing/services'
import { ContactSourceUpdatePayload } from '../../../modules/Marketing/types/payload'
import {
    ContactChanel,
    IndustryOptions,
} from '../../../modules/Marketing/types/utils'
import {
    getClassifyLeadStatusLabel,
    getClassifyMissingFields,
    getClassifyRequiredFieldsSummary,
} from '../../../modules/Marketing/utils/classify-lead'
import {
    getAlertColors,
    getLeadAlert,
} from '../../../modules/Marketing/utils/lead-alerts'
import { useContactDetailQuickHelp } from './use-contact-detail-quick-help'
import LeadSourceTrackingPanel from './LeadSourceTrackingPanel'
import { CONTACT_ASSIGNEE_DEPARTMENT_IDS } from '@/constants/departments'

const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

interface ContactDetailModalProps {
    open: boolean
    onClose: () => void
    contactSourceID: number | null
}

// Lead classification config with Button variants
const LEAD_STATUS_CONFIG = {
    'warm-lead': {
        label: 'Warm Lead',
        icon: Flame,
        variant: 'outline-warning' as const,
    },
    'sale-qualified-lead': {
        label: 'SQL',
        icon: CheckCircle2,
        variant: 'outline-success' as const,
    },
    'cool-lead': {
        label: 'Cool Lead',
        icon: Snowflake,
        variant: 'outline-info' as const,
    },
    'un-qualified-lead': {
        label: 'Unqualified',
        icon: Ban,
        variant: 'outline-destructive' as const,
    },
} as const

const ContactDetailModal: React.FC<ContactDetailModalProps> = ({
    open,
    onClose,
    contactSourceID,
}) => {
    const { t } = useTranslation()
    const { t: tMar } = useTranslation('marketing')
    const { t: tAxiosError } = useTranslation('axios-error')
    const { employee } = useAuthStore()
    const { refetchCurrent } = useDataGridRefetch({
        reactQueryKey: 'contact-source-list',
    })
    const [isAssigning, setIsAssigning] = useState(false)
    // Register quick help options for contact detail workflow (only when modal is open)
    useContactDetailQuickHelp({ enabled: open })

    const [activeTab, setActiveTab] = useState('logs')

    // Reset tab when modal opens
    React.useEffect(() => {
        if (open) setActiveTab('logs')
    }, [open, contactSourceID])

    const { refetch: refetchActivities } = useQueryClientCallback({
        pattern: 'activities',
        mode: 'contains',
    })
    const {
        data: contactSource,
        refetch: callbackContactSource,
        renderState,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTACT_SOURCE_DETAIL, contactSourceID],
        queryFn: async () => {
            const result = await getContactSourceByID({
                contact_source_id: Number(contactSourceID) || 0,
            })
            return result
        },
        enabled: !!contactSourceID && open,
    })

    const formControl = useForm<ContactSourceUpdatePayload>({
        defaultValues: {
            contact_source_name: '',
            website_referrer: '',
            contact_source_position: '',
            contact_source_phone: '',
            contact_source_email: '',
            contact_source_company: '',
            contact_source_website: '',
            contact_source_profile: '',
            contact_source_industry: '',
            contact_source_channel: '',
            contact_source_interested_services: [],
            contact_source_status: '',
            contact_source_notes: '',
            contact_source_assignee_ids: [],
        },
    })

    // Reset form when data loads
    React.useEffect(() => {
        if (contactSource?.result) {
            formControl.reset({
                contact_source_name:
                    contactSource.result.contact_source_name || '',
                contact_source_position:
                    contactSource.result.contact_source_position || '',
                contact_source_phone:
                    contactSource.result.contact_source_phone || '',
                contact_source_email:
                    contactSource.result.contact_source_email || '',
                contact_source_company:
                    contactSource.result.contact_source_company || '',
                contact_source_website:
                    contactSource.result.contact_source_website || '',
                website_referrer: contactSource.result.website_referrer || '',
                contact_source_profile:
                    contactSource.result.contact_source_profile || '',
                contact_source_industry:
                    contactSource.result.contact_source_industry || '',
                contact_source_channel:
                    contactSource.result.contact_source_channel || '',
                contact_source_interested_services:
                    (typeof contactSource.result
                        .contact_source_interested_services === 'string'
                        ? JSON.parse(
                              contactSource.result
                                  .contact_source_interested_services
                          )
                        : contactSource.result
                              .contact_source_interested_services) || [],
                contact_source_status:
                    contactSource.result.contact_source_status || '',
                contact_source_notes:
                    contactSource.result.contact_source_notes || '',
                contact_source_assignee_ids:
                    contactSource.result.contact_source_assignee_ids || [],
            })
        }
    }, [contactSource, formControl])

    // Payload cập nhật dùng chung cho mutation (nút Save) và pre-save im lặng (classify)
    const buildContactSourceUpdatePayload = (
        values: ContactSourceUpdatePayload
    ) => ({
        contact_source_id: Number(contactSourceID),
        contact_source_name: values.contact_source_name,
        contact_source_email: values.contact_source_email,
        contact_source_type: values.contact_source_type,
        contact_source_channel: values.contact_source_channel,
        contact_source_company: values.contact_source_company,
        contact_source_messages: values.contact_source_messages,
        contact_source_assignee_ids: values.contact_source_assignee_ids,
        contact_source_phone: values.contact_source_phone,
        contact_source_website: values.contact_source_website,
        contact_source_notes: values.contact_source_notes,
        contact_source_status: values.contact_source_status,
        contact_source_position: values.contact_source_position,
        contact_source_profile: values.contact_source_profile,
        contact_source_industry: values.contact_source_industry,
        contact_source_interested_services:
            values.contact_source_interested_services,
        website_referrer: values.website_referrer,
    })

    const {
        mutateAsync: updateContactSourceMutation,
        isPending: isUpdateContactSourcePending,
    } = useMutationWithHandler(
        {
            mutationFn: (values: ContactSourceUpdatePayload) =>
                updateContactSource(buildContactSourceUpdatePayload(values)),
        },
        {
            reactQueryKeys: [QUERY_KEYS.MARKETING, QUERY_KEYS.STATICS],
            successMessage: t('updated-success'),
            onSuccess: () => {
                callbackContactSource()
                refetchCurrent()
                refetchActivities()
            },
        }
    )

    // Classify mutation
    const { mutateAsync: classifyMutation, isPending: isClassifying } =
        useMutationWithHandler(
            {
                mutationFn: (data: {
                    contact_source_id: number
                    status: LeadStatus
                }) =>
                    classifyContactSource({
                        contact_source_id: data.contact_source_id,
                        lead_status: data.status,
                    }),
            },
            {
                reactQueryKeys: QUERY_KEYS.MARKETING,
                successMessage: tMar('classify-success'),
                // Handle errors manually so we can interpolate the conflicting
                // email into the message. BE returns kebab code
                // "customer-email-already-exists" + an `email` field on 409.
                showErrorToast: false,
                onError: (error) => {
                    const data = (
                        error as {
                            response?: {
                                data?: {
                                    error_description?: string
                                    email?: string
                                }
                            }
                        }
                    )?.response?.data
                    const code = data?.error_description
                    if (code === 'customer-email-already-exists') {
                        toast.error(
                            tMar('customer-email-already-exists', {
                                email: data?.email ?? '',
                            })
                        )
                        return
                    }
                    // Preserve prior behavior for other errors: translate the
                    // kebab code via the shared axios-error dict, else fall back
                    // to the generic classify error.
                    const translated = code
                        ? tAxiosError(code, { defaultValue: '' })
                        : ''
                    toast.error(translated || tMar('classify-error'))
                },
                onSuccess: () => {
                    callbackContactSource()
                    refetchCurrent()
                    refetchActivities()
                },
            }
        )

    const handleClassify = async (status: LeadStatus) => {
        if (!contactSourceID) return

        const formValues = formControl.getValues()
        const missing = getClassifyMissingFields(status, formValues, tMar)
        if (missing.length > 0) {
            toast.error(
                tMar('classify-missing-fields', {
                    status: getClassifyLeadStatusLabel(status, tMar),
                    fields: missing.join(', '),
                })
            )
            return
        }

        // Lưu thay đổi trước khi classify — im lặng (không toast) để chỉ hiện 1 toast
        // kết quả từ bước classify. Dùng service thô vì mutateAsync nuốt lỗi;
        // service thô throw nên try/catch bắt được lỗi thật.
        if (formControl.formState.isDirty) {
            try {
                await updateContactSource(
                    buildContactSourceUpdatePayload(formValues)
                )
            } catch {
                toast.error(tMar('classify-error'))
                return
            }
        }

        await classifyMutation({
            contact_source_id: Number(contactSourceID),
            status,
        })
    }

    // Assign to current user
    const handleAssignToMe = async () => {
        if (!contactSourceID || !employee?.employee_id) return

        setIsAssigning(true)
        try {
            const currentAssignees =
                formControl.getValues('contact_source_assignee_ids') || []
            // Add current employee if not already assigned
            if (!currentAssignees.includes(employee.employee_id)) {
                const newAssignees = [...currentAssignees, employee.employee_id]
                await updateContactSource({
                    contact_source_id: Number(contactSourceID),
                    contact_source_assignee_ids: newAssignees,
                })
                formControl.setValue(
                    'contact_source_assignee_ids',
                    newAssignees
                )
                await callbackContactSource()
                refetchCurrent()
                refetchActivities()
                toast.success(t('assign-to-me-success'))
            } else {
                toast.error(t('already-assigned-to-you'))
            }
        } catch {
            toast.error(t('assign-to-me-error'))
        } finally {
            setIsAssigning(false)
        }
    }

    // Check assignees
    const assigneeIds = contactSource?.result?.contact_source_assignee_ids || []
    const hasAssignees = assigneeIds.length > 0

    // Can classify: has assignees, not converted to customer, not MATCHED/SPAM
    // Converted leads (warm/sql/cool) already created a Customer → locked
    // Un-qualified leads did NOT create a Customer → allow reclassify
    const isAlreadyConverted = contactSource?.result?.converted === true
    const canClassify =
        hasAssignees &&
        !isAlreadyConverted &&
        !['MATCHED', 'SPAM'].includes(
            contactSource?.result?.contact_source_status || ''
        )

    const channel = contactSource?.result?.contact_source_channel
    const isLiveChat =
        channel === 'livechat' ||
        channel === 'LIVE_CHAT' ||
        channel === 'fb_messenger' ||
        channel === 'FB_MESSENGER'

    const objectType = contactSource?.result?.contact_source_object_type
    const objectId = String(
        contactSource?.result?.contact_source_object_id || ''
    )
    const isInternalLivechat =
        objectType === 'Internal-Livechat' ||
        (isLiveChat && UUID_REGEX.test(objectId))

    const isSpam = contactSource?.result?.contact_source_status === 'SPAM'
    const currentStatus = contactSource?.result?.contact_source_status
    const isFormDisabled = isAlreadyConverted || isSpam

    // Chỉ lead thật sự gắn khách mới có nút "Đi tới khách hàng": referrer_id = -1 (lead tạo từ form)
    // và referrer kiểu 'Employee' (dữ liệu cũ) đều không phải customer_id.
    const referrerId = contactSource?.result?.contact_source_referrer_id
    const linkedCustomerId =
        contactSource?.result?.contact_source_referrer_type ===
            'Reducer::Customer' &&
        referrerId &&
        referrerId > 0
            ? referrerId
            : null

    const wrappedApiCall = React.useMemo(
        () =>
            ({ page, page_size }: ActivityPayload) =>
                getContactSourceUpdates({
                    contactSourceID: Number(contactSourceID || 0),
                    payload: {
                        page,
                        page_size,
                    },
                }),
        [contactSourceID]
    )

    const tabs = React.useMemo(() => {
        // Chat history component - show message if channel not supported
        const chatHistoryComponent = (() => {
            if (!isLiveChat) {
                return (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <svg
                            className="w-12 h-12 mb-3 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                            />
                        </svg>
                        <p className="text-sm font-medium">
                            {t('marketing:chat_history_not_available')}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                            {t('marketing:chat_history_channel_not_supported')}
                        </p>
                    </div>
                )
            }
            if (!contactSource?.result?.contact_source_object_id) {
                return (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <svg
                            className="w-12 h-12 mb-3 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                            />
                        </svg>
                        <p className="text-sm font-medium">
                            {t('marketing:no_chat_history')}
                        </p>
                    </div>
                )
            }

            // Internal-Livechat: objectId is room UUID → use LivechatConversationPreview
            if (isInternalLivechat) {
                return (
                    <div className="max-h-[500px] overflow-y-auto border border-gray-200 rounded-xl shadow-sm">
                        <LivechatConversationPreview roomId={objectId} />
                    </div>
                )
            }

            // External conversation: objectId is numeric → use ContactConversation
            const assignee = contactSource?.result?.contact_source_assignee
            const mappedAssignee = assignee
                ? {
                      id: String(assignee.employee_id),
                      name: assignee.employee_name,
                      email: assignee.email,
                      type: 'employee',
                      avatar: '',
                  }
                : undefined

            return (
                <div className="max-h-[500px] overflow-y-auto border border-gray-200 rounded-xl shadow-sm">
                    <ContactConversation
                        conversationId={Number(
                            contactSource?.result?.contact_source_object_id
                        )}
                        assignee={mappedAssignee}
                    />
                </div>
            )
        })()

        return [
            {
                label: t('action-log'),
                value: 'logs',
                startIcon: <InfoIcons className="text-bg-primary" />,
                component: (
                    <Activities
                        apiCall={wrappedApiCall}
                        queryKey={`contact-source.${contactSourceID}.updates`}
                    />
                ),
            },
            {
                label: t('live-chat-messengers'),
                value: 'chat-history',
                startIcon: (
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                        />
                    </svg>
                ),
                component: chatHistoryComponent,
            },
        ]
    }, [
        t,
        isLiveChat,
        isInternalLivechat,
        objectId,
        contactSource?.result?.contact_source_object_id,
        contactSource?.result?.contact_source_assignee,
        wrappedApiCall,
        contactSourceID,
    ])

    const TabComponent = tabs.find((tab) => tab.value === activeTab)?.component
    const TabProps = {
        tabs: tabs.map((item) => ({
            ...item,
            label: item.label,
        })),
        activeTab,
        onChangeTab: setActiveTab,
        color: 'info' as const,
        tabClassName: 'uppercase text-gray-500 [&.text-info]:!text-primary',
    }

    if (!contactSourceID) return null

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    // Get current lead status for highlighting
    const getStatusBadge = () => {
        const status = currentStatus
        if (status === 'warm-lead') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    <Flame size={12} /> Warm Lead
                </span>
            )
        }
        if (status === 'sale-qualified-lead') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <CheckCircle2 size={12} /> SQL
                </span>
            )
        }
        if (status === 'cool-lead') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    <Snowflake size={12} /> Cool Lead
                </span>
            )
        }
        if (status === 'un-qualified-lead') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    <Ban size={12} /> Unqualified
                </span>
            )
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                {status || t('unclassified')}
            </span>
        )
    }

    return (
        <Drawer open={open} onClose={onClose} width="80%">
            <div className="px-4 pb-8 pt-4">
                {/* Header */}
                <div className="flex justify-between items-center sticky top-0 bg-white z-10 pb-2">
                    <div className="flex items-center gap-3">
                        <CustomerIcons className="w-5 h-5 text-primary" />
                        <div>
                            <Typogaphy className="font-medium text-primary">
                                {t('customer-mkt')} - SID:{' '}
                                {contactSource?.result?.contact_source_id}
                            </Typogaphy>
                            <div className="flex items-center gap-2 mt-1">
                                {getStatusBadge()}
                                <span className="text-[11px] text-gray-400">
                                    {t('lasted_update')}:{' '}
                                    {formatDateWithFormatter(
                                        contactSource?.result
                                            ?.contact_source_updated_at ?? '',
                                        'DD/MM/YYYY HH:mm'
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {linkedCustomerId && (
                            <Link
                                to={generatePathDatacentral(
                                    paths.customerDetail,
                                    {
                                        id: linkedCustomerId,
                                    }
                                )}
                                data-tour="view-customer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-full transition-colors"
                                onClick={onClose}
                            >
                                <ExternalLink size={12} />
                                {t('go_to_customer')}
                            </Link>
                        )}
                        <IconButton
                            size="small"
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <CloseIcons />
                        </IconButton>
                    </div>
                </div>

                {/* Two-column layout: Lead Flow (left) + Form (right) */}
                <div className="flex gap-4 mt-2">
                    {/* Left: Lead Conversion Flow */}
                    <div className="w-[340px] shrink-0">
                        <div className="lead-conversion-flow sticky top-12">
                            <LeadConversionFlowCompact
                                currentStatus={currentStatus}
                                isConverted={isAlreadyConverted}
                                hasAssignees={hasAssignees}
                            />
                        </div>
                    </div>

                    {/* Right: Alerts + Form + Actions + Tabs */}
                    <div className="flex-1 min-w-0">
                        {/* Lead Alert Banner */}
                        {contactSource?.result &&
                            (() => {
                                const alert = getLeadAlert(contactSource.result)
                                if (!alert) return null
                                const colors = getAlertColors(alert.type)
                                if (!colors) return null
                                const Icon =
                                    alert.type === 'unassigned' ? UserX : Clock

                                return (
                                    <div
                                        className={`mb-3 p-2.5 ${colors.bg} border ${colors.border} rounded-md`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Icon
                                                className={`w-4 h-4 ${colors.icon} shrink-0`}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <Typogaphy
                                                    className={`text-xs font-semibold ${colors.text}`}
                                                >
                                                    {alert.type === 'unassigned'
                                                        ? t(
                                                              'lead-alert-unassigned-title'
                                                          )
                                                        : t(
                                                              'lead-alert-stale-title'
                                                          )}
                                                </Typogaphy>
                                                <p
                                                    className={`text-[11px] ${colors.text} opacity-80`}
                                                >
                                                    {t(alert.message, {
                                                        days: alert.daysOverdue,
                                                    })}
                                                </p>
                                            </div>
                                            <span
                                                className={`text-xs font-medium ${colors.text} px-2 py-0.5 rounded ${colors.bg} shrink-0`}
                                            >
                                                {alert.daysOverdue} {t('days')}
                                            </span>
                                        </div>
                                    </div>
                                )
                            })()}

                        {/* SPAM Warning */}
                        {isSpam && (
                            <div className="mb-3">
                                <div className="bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500 p-3 rounded-lg shadow-sm">
                                    <div className="flex items-center gap-2">
                                        <X className="w-5 h-5 text-red-500 shrink-0" />
                                        <div className="flex-1">
                                            <Typogaphy className="text-xs font-semibold text-red-800">
                                                {t('contact-is-spam')}
                                            </Typogaphy>
                                            <p className="text-[11px] text-red-700 mt-0.5">
                                                {t(
                                                    'contact-is-spam-description'
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Converted Success */}
                        {isAlreadyConverted && (
                            <div className="mb-3">
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-3 rounded-lg shadow-sm">
                                    <div className="flex items-center gap-2">
                                        <Check className="w-5 h-5 text-green-500 shrink-0" />
                                        <div className="flex-1">
                                            <Typogaphy className="text-xs font-semibold text-green-800">
                                                {t(
                                                    'converted-to-customer-success'
                                                )}
                                            </Typogaphy>
                                            <p className="text-[11px] text-green-700 mt-0.5">
                                                {t(
                                                    'converted-to-customer-success-description'
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Form */}
                        <div className="contact-form-fields">
                            <FormProvider {...formControl}>
                                <GridHookForm
                                    formControl={formControl}
                                    onSubmit={updateContactSourceMutation}
                                    actions={[]}
                                    data={[
                                        {
                                            formItemProps: {
                                                name: 'contact_source_name',
                                                type: 'textfield',
                                                label: t('contact-source-name'),
                                                placeholder: t(
                                                    'contact-source-name'
                                                ),
                                                disabled: isFormDisabled,
                                            },
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            formItemProps: {
                                                name: 'contact_source_position',
                                                type: 'textfield',
                                                label: t('position'),
                                                placeholder: t('position'),
                                                disabled: isFormDisabled,
                                            },
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            formItemProps: {
                                                name: 'contact_source_phone',
                                                type: 'textfield',
                                                label: t(
                                                    'contact-source-phone'
                                                ),
                                                placeholder: t(
                                                    'contact-source-phone'
                                                ),
                                                disabled: isFormDisabled,
                                            },
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            renderEditComponent: (control) => (
                                                <EmailFieldUpdate
                                                    control={control}
                                                    initialEmail={
                                                        contactSource?.result
                                                            ?.contact_source_email ||
                                                        ''
                                                    }
                                                    disabled={isFormDisabled}
                                                />
                                            ),
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            formItemProps: {
                                                name: 'contact_source_company',
                                                type: 'textfield',
                                                label: t('company'),
                                                placeholder: t('company'),
                                                disabled: isFormDisabled,
                                            },
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            formItemProps: {
                                                name: 'contact_source_website',
                                                type: 'textfield',
                                                label: t('Website'),
                                                placeholder: t('Website'),
                                                disabled: isFormDisabled,
                                            },
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            formItemProps: {
                                                name: 'contact_source_profile',
                                                type: 'textfield',
                                                label: t(
                                                    'contact-source-profile'
                                                ),
                                                placeholder: t(
                                                    'contact-source-profile'
                                                ),
                                                disabled: isFormDisabled,
                                            },
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            formItemProps: {
                                                name: 'contact_source_industry',
                                                type: 'select',
                                                label: t(
                                                    'contact-source-industry'
                                                ),
                                                placeholder: t(
                                                    'contact-source-industry'
                                                ),
                                                options: IndustryOptions,
                                                disabled: isFormDisabled,
                                                allowClear: false,
                                            },
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            formItemProps: {
                                                name: 'contact_source_channel',
                                                type: 'select',
                                                label: t(
                                                    'contact-source-channel'
                                                ),
                                                placeholder: t(
                                                    'contact-source-channel'
                                                ),
                                                options: ContactChanel(tMar),
                                                allowClear: false,
                                                disabled:
                                                    isFormDisabled ||
                                                    !formControl?.watch(
                                                        'contact_source_status'
                                                    ),
                                            },
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            renderEditComponent: () => (
                                                <ProductGroupSelect
                                                    control={
                                                        formControl?.control
                                                    }
                                                    name="contact_source_interested_services"
                                                    multiple
                                                    showLabel
                                                    label={t(
                                                        'contact-source-interested-services'
                                                    )}
                                                    placeholder={t(
                                                        'contact-source-interested-services'
                                                    )}
                                                />
                                            ),
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            formItemProps: {
                                                name: 'website_referrer',
                                                type: 'textfield',
                                                label: t('website-referrer'),
                                                placeholder:
                                                    t('website-referrer'),
                                                disabled: isFormDisabled,
                                            },
                                            className: 'md:col-span-2',
                                        },
                                        {
                                            formItemProps: {
                                                name: 'contact_source_notes',
                                                type: 'textarea',
                                                label: t('note'),
                                                placeholder: t(
                                                    'note-placeholder-spam'
                                                ),
                                                disabled: isFormDisabled,
                                            },
                                            className: 'md:col-span-4',
                                        },
                                        {
                                            renderEditComponent: (control) => (
                                                <EmployeeSelect
                                                    control={control}
                                                    name="contact_source_assignee_ids"
                                                    multiple
                                                    showLabel
                                                    label="marketing:contact-source-assignee"
                                                    departmentIds={
                                                        CONTACT_ASSIGNEE_DEPARTMENT_IDS
                                                    }
                                                    disabled={isFormDisabled}
                                                />
                                            ),
                                            className: 'md:col-span-4',
                                        },
                                    ]}
                                />
                            </FormProvider>
                        </div>

                        {/* Lead-origin data + campaign attribution (read-only) */}
                        {contactSource?.result && (
                            <LeadSourceTrackingPanel
                                contactSource={contactSource.result}
                            />
                        )}

                        {/* Required Fields Legend */}
                        {canClassify && (
                            <div className="mt-3 p-3 bg-blue-50/60 border border-blue-200 rounded-md">
                                <p className="text-xs font-semibold text-blue-800 mb-1.5">
                                    {tMar('classify-required-fields')}
                                </p>
                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                    {getClassifyRequiredFieldsSummary(tMar).map(
                                        (group) => {
                                            const Icon =
                                                LEAD_STATUS_CONFIG[
                                                    group.statuses[0]
                                                ]?.icon
                                            return (
                                                <span
                                                    key={group.label}
                                                    className="text-[11px] text-blue-700 flex items-center gap-1"
                                                >
                                                    {Icon && <Icon size={12} />}
                                                    <strong>
                                                        {group.label}:
                                                    </strong>{' '}
                                                    {group.fields.join(', ')}
                                                </span>
                                            )
                                        }
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Actions Row: Assign to Me + Classify + Save */}
                        {!isFormDisabled && (
                            <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                                <div className="flex flex-wrap items-center gap-2 justify-end">
                                    {!hasAssignees && (
                                        <VNButton
                                            data-tour="assign-to-me"
                                            variant="outlined"
                                            onClick={handleAssignToMe}
                                            disabled={isAssigning}
                                            loading={isAssigning}
                                            startIcon={<UserPlus size={16} />}
                                        >
                                            {t('assign-to-me')}
                                        </VNButton>
                                    )}

                                    {canClassify && (
                                        <div
                                            className="flex flex-wrap gap-2"
                                            data-tour="classify-buttons"
                                        >
                                            {(
                                                Object.entries(
                                                    LEAD_STATUS_CONFIG
                                                ) as [
                                                    LeadStatus,
                                                    (typeof LEAD_STATUS_CONFIG)[LeadStatus],
                                                ][]
                                            ).map(([status, config]) => {
                                                const IconComponent =
                                                    config.icon
                                                const isLoading =
                                                    isClassifying ||
                                                    isUpdateContactSourcePending
                                                return (
                                                    <Button
                                                        key={status}
                                                        variant={config.variant}
                                                        onClick={() =>
                                                            handleClassify(
                                                                status
                                                            )
                                                        }
                                                        disabled={isLoading}
                                                    >
                                                        {isLoading ? (
                                                            <Loader2
                                                                className="animate-spin"
                                                                size={16}
                                                            />
                                                        ) : (
                                                            <IconComponent
                                                                size={16}
                                                            />
                                                        )}
                                                        {config.label}
                                                    </Button>
                                                )
                                            })}
                                        </div>
                                    )}

                                    <VNButton
                                        variant="contained"
                                        onClick={formControl.handleSubmit(
                                            updateContactSourceMutation
                                        )}
                                        loading={isUpdateContactSourcePending}
                                        disabled={
                                            !formControl.formState.isDirty ||
                                            isUpdateContactSourcePending
                                        }
                                    >
                                        {t('save')}
                                    </VNButton>
                                </div>
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="mt-4">
                            <Tabs {...TabProps} />
                            {TabComponent}
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    )
}

export default ContactDetailModal
